import { INumericInputProps, NumericInput } from 'Components/Core';
import { IWithInjectedProps } from 'Components/Core/InjectedPropsContext';
import { RootState } from 'Data/Redux';
import { actions } from 'Data/ReduxViewModels/EquipmentInterfacing';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { usePrevious } from 'Lib/Hooks';
import { wrapHigherOrderComponent } from 'Lib/React';
import { resolveSpecialCharacterSequences } from 'Lib/String';
import { isWebSerialApiSupported } from 'Lib/WebSerialApi/helpers';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { tabbable } from 'tabbable';
import { InterfaceDialog, InterfaceIndicator } from '.';

/**
 * Props required for inputs that support interfacing
 */
type InterfacingInputProps = Pick<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'disabled' | 'onKeyDown' | 'onFocus' | 'onBlur' | 'value'
> &
  Pick<INumericInputProps, 'severity' | 'focusRef'> &
  Pick<IWithInjectedProps, 'measuredUsing'> & { onChange?: (_: any) => void };

let idCounter = 0;
/**
 * Higher-order-component that wraps an input component with equipment interfacing functionality
 *
 * @param Component The component to wrap. Note: Components other than NumericInput will be passed back unchanged.
 */
const withInterfacing = <P extends InterfacingInputProps>(Component: React.ComponentType<P>): React.FC<P> => {
  const isInterfacingEnabled = isWebSerialApiSupported();

  // Do not wrap fields if interfacing is not enabled
  if (!isInterfacingEnabled) return (p) => <Component {...p} />;

  return wrapHigherOrderComponent(
    (props: P) => {
      const { measuredUsing, disabled, onKeyDown, onChange, onFocus, onBlur, value, severity, focusRef } = props;
      const equipmentId = measuredUsing?.Id;

      const [requestId] = useState(`interfaceRequest-${idCounter++}`);
      const [showInterfaceDialog, setShowInterfaceDialog] = useState(false);
      const [awaitingValue, setAwaitingValue] = useState(false);
      /**
       * Keep track of whether the last dialog's value was accepted (so we can respond to it after closing)
       */
      const [lastDialogValueAccepted, setLastDialogValueAccepted] = useState(false);
      const inputReference = useRef<HTMLInputElement>(null);
      const dispatch = useDispatch();

      const requestDataRecorded = useSelector((s) => hasValue(s.equipmentInterfacing.requests[requestId]));
      const requestInProgress = useSelector(
        (s) => s.equipmentInterfacing.requests[requestId]?.obtainValue.isInProgress ?? false
      );
      const requestComplete = useSelector(
        (s) => s.equipmentInterfacing.requests[requestId]?.obtainValue?.complete ?? false
      );
      const obtainedValue = useSelector((s) => s.equipmentInterfacing.requests[requestId]?.obtainValue.obtainedValue);

      const canInterface = useSelector(
        (state: RootState) =>
          hasNonEmptyValue(measuredUsing?.Driver) &&
          state.equipmentInterfacing.equipment[equipmentId]?.isConnected &&
          !disabled &&
          // While other types can technically support interfacing, we want to limit scope of the inputs supported
          Component === NumericInput
      );

      /**
       * Set focus onto next element when interfacing dialog has been closed
       *
       * We cannot handle this within the dialog due to its focus trap, so instead
       * we listen for it being closed and respond here.
       */
      const prevShowInterfaceDialog = usePrevious(showInterfaceDialog);
      useEffect(() => {
        if (prevShowInterfaceDialog && !showInterfaceDialog) {
          // Move focus to next element if value was accepted.
          if (lastDialogValueAccepted) {
            // If the field has an onKeyDown handler defined, assume this will handle tabbing behaviour
            // and submit a tab event through that handler
            // e.g. for handling tab order within a grid
            if (hasValue(onKeyDown)) {
              onKeyDown!(new KeyboardEvent('keydown', { key: 'Tab' }) as any);
            }
            // Else try and advance to the next tabbable element
            else if (document.activeElement != null) {
              var tabbableElements = tabbable(document.activeElement);
              var index = tabbableElements.findIndex((t) => t === (inputReference as any).current);
              tabbableElements[index + 1]?.focus();
            }
          }
          // If value was not accepted return focus to this element
          else {
            ((focusRef as any) ?? inputReference)?.current?.focus();
          }
        }
      }, [prevShowInterfaceDialog, showInterfaceDialog, lastDialogValueAccepted, focusRef, inputReference, onKeyDown]);

      useEffect(() => {
        return () => {
          if (requestDataRecorded) dispatch(actions.cleanupRequest({ equipmentId, requestId: requestId }));
        };
      }, [dispatch, equipmentId, requestId, requestDataRecorded]);

      useEffect(() => {
        if (awaitingValue && requestComplete && onChange) {
          onChange({ data: { value: obtainedValue, submitValues: true } });
          setAwaitingValue(false);
        }

        if (awaitingValue && !requestInProgress) {
          setAwaitingValue(false);
        }
        // TODO: onChange temporarily removed from dependancy array as it will change on every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [requestComplete, obtainedValue, requestInProgress, awaitingValue]);

      // If we can't interface, just return the original component
      if (!canInterface) return <Component {...props} />;

      const interceptOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!hasNonEmptyValue(value) && !awaitingValue) {
          dispatch(
            actions.obtainValue({
              equipmentId,
              requestId: requestId,
              options: measuredUsing.Options,
              regexPattern: measuredUsing?.Driver?.Data.Regex,
              send: resolveSpecialCharacterSequences(measuredUsing?.Driver?.Data.Request),
            })
          );
          setAwaitingValue(true);
        }
        onFocus?.(e);
      };

      const interceptOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === ' ' && canInterface && !e.defaultPrevented) {
          setAwaitingValue(false); // Dialog will take ownership of request once shown.
          setShowInterfaceDialog(!showInterfaceDialog);
          e.preventDefault();
        } else {
          onKeyDown?.(e);
        }
      };

      const interceptOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (requestInProgress && !showInterfaceDialog)
          dispatch(
            actions.readCancel({
              requestId,
              equipmentId,
            })
          );
        onBlur?.(e);
      };

      return (
        <>
          <Component
            {...props}
            focusRef={focusRef ?? inputReference} //If element has an existing reference then don't replace it
            onKeyDown={interceptOnKeyDown}
            onFocus={interceptOnFocus}
            onBlur={interceptOnBlur}
          />
          <InterfaceIndicator inProgress={requestInProgress && !showInterfaceDialog} severity={severity} />
          {showInterfaceDialog && (
            <InterfaceDialog
              {...props}
              visible={true}
              requestId={requestId}
              equipment={measuredUsing}
              equipmentType={measuredUsing?.Driver?.Data.EquipmentType.Name}
              onClose={(e) => {
                setShowInterfaceDialog(false);
                setLastDialogValueAccepted(e?.data.valueAccepted ?? false);
              }}
            />
          )}
        </>
      );
    },
    Component,
    'WithInterfacing'
  );
};

export { withInterfacing };
export default withInterfacing;
